import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  FormControl,
  Grid,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
} from '@mui/material';

import PageLayout from '../../../../Containers/PageLayout';
import { ROUTES } from '../../../../helpers/contants';
import { useListing } from '../../../../hooks/useListing';
import endPoints from '../../../../api/endpoint';
import '../../../RiskCenter/service.scss';
import {
  setRiskBreadCrumbs,
  updatePaginationPage,
} from '../../../../redux/commonSlice';
import HeadingRow from '../../../Dashboard/components/HeadingRow';
import AddButton from '../../../../Components/AddButton';
import InfiniteScroll from '../../../../Components/InfiniteScrollComponent';
import NoData from '../../../../Components/Nodata';
import TableLoader from '../../../Dashboard/components/SkeletonLoader/TableLoader';
import {
  TabProps,
  formatStringToTitleCase,
  useDebounce,
  getTimeWithTimeZoneAbbreviationNew,
} from '../../../../Utils/helperFns';
import Images from '../../../../Utils/images';
import ThreatCard from '../ThreatCard';
import '../../RiskCenter.scss';
import AutocompleteSelect from '../../../../Components/AutocompleteSelect';
import RiskCardLoader from '../RiskCardLoader';
import ToolTipComponent from '../../../../Components/ToolTipComponent';
import TablePagination from '../../../../Components/Pagination';
import MUIAutoComplete from '../../../../Components/MUIAutoComplete';
import dayjs from 'dayjs';

const ViewAllEpisodeSets = () => {
  const [value, setValue] = useState(0);
  const { episodeId, status, name } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const episodeName = searchParams.get('episodeName');
  const productionType = searchParams.get('productionType');
  const studioId = searchParams.get('studioId');

  const [tabCounts, setTabCounts] = useState<any>({});
  const [searchKey, setSearchKey] = useState<string>('');
  const [isTabular, setIsTabular] = useState<boolean>(false);
  const [threatFilter, setThreatFilter] = useState<string | null>('ALL');
  const [filter, setFilter] = useState<any>({
    page: 1,
    limit: 8,
    searchKey: '',
    riskLevel: '',
  });
  const [dropdownfilter, setDropdownFilter] = useState<any>({
    page: 1,
    limit: 10,
  });
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 500);

  const {
    fetchListing,
    data: episodeSetList,
    hasMore,
    loading,
    setData,
    count,
  } = useListing<any>({
    url: endPoints.riskSecurity.episodeDashboardSetThreats.replace(
      '{episodeId}',
      episodeId || '',
    ),
    params: {
      ...filter,
      search: filter.search || '',
      setStatus:
        value === 0
          ? 'ACTIVE'
          : value === 1
          ? 'COMPLETED'
          : value === 2
          ? 'INACTIVE'
          : 'ALL',
    },
    isInfinite: !isTabular,
    isRiskDashboard: true,
    setCountData: setTabCounts,
  });

  const {
    fetchListing: fetchSetListingDropdown,
    data: setDropdownList,
    hasMore: hasMoreSets,
    loading: loadingSets,
  } = useListing<any>({
    url: endPoints.riskSecurity.episodeDashboardSetThreats.replace(
      '{episodeId}',
      episodeId || '',
    ),
    params: {
      ...dropdownfilter,
      // search: filter.search || '',
    },
    isInfinite: !isTabular,
    isRiskDashboard: true,
    setCountData: setTabCounts,
  });

  useEffect(() => {
    fetchListing();
  }, [filter, value]);

  const loadMore = () => {
    if (!loading && hasMore) {
      setFilter({
        ...filter,
        page: filter.page + 1,
      });
    }
  };

  useEffect(() => {
    updatePaginationPage(1);
  }, []);

  const showBreadCrumb = () => {
    return (
      <>
        <ul className="breadCrumbs">
          <li>
            <a href={`${ROUTES.RISK_CENTER}?${episodeId}`}>
              {episodeName || name || 'Episode Dashboard'}
            </a>
          </li>
          <li>{`Threat Level By ${
            productionType?.toLowerCase()?.includes('event')
              ? 'Activity'
              : 'Set'
          }`}</li>
        </ul>
      </>
    );
  };

  const filterOptions = [
    { id: '', label: 'All' },
    { id: 'CRITICAL', label: 'Critical' },
    { id: 'HIGH', label: 'High' },
    { id: 'MEDIUM', label: 'Medium' },
    { id: 'LOW', label: 'Low' },
  ];

  const ALL_OPTION = { label: 'All', value: 'ALL' };

  useEffect(() => {
    fetchSetListingDropdown();
  }, [dropdownfilter]);

  useEffect(() => {
    setDropdownFilter(prev => ({
      ...prev,
      page: 1, // 🔥 reset pagination on search
      searchKey: debouncedSearch,
    }));
  }, [debouncedSearch]);

  return (
    <PageLayout>
      <section className="store-inner">
        <HeadingRow
          title={`Threat Level By ${
            productionType?.toLowerCase()?.includes('event')
              ? 'ActivitIES'
              : 'SetS'
          }`}
          setData={setData}
          onSearch={(value: string) => {
            setFilter({
              ...filter,
              searchKey: value.trim(),
              page: 1,
            });
          }}
          handleClearSearch={() => {
            updatePaginationPage(1);
            setData([]);
            setThreatFilter('ALL');
            setFilter({
              ...filter,
              searchKey: '',
              page: 1,
            });
          }}
          isTabular={isTabular}
          setIsTabular={setIsTabular}
          setFilter={() => {
            setData([]);
            setFilter({
              ...filter,
              page: 1,
              searchKey: '',
            });
          }}
          isManage
          isShowViewBtn={count > 0}
          searchKey={searchKey}
          setSearchKey={setSearchKey}
          showBreadCrumb={showBreadCrumb}
          isShowBreadcrumb
          searchPlaceholder={`Search ${
            productionType?.toLowerCase()?.includes('event')
              ? 'Activities'
              : 'Sets'
          }...`}
          episodeId={episodeId}
          studioName={name}
        />

        <div className="left">
          <Grid container spacing={1}>
            <Grid item md={4} xs={12}>
              <div className="form-in lg">
                <MUIAutoComplete
                  name="studio"
                  id={'studio'}
                  type="riskStudio"
                  keyName="name"
                  options={[
                    ALL_OPTION, // ✅ default All option
                    ...(setDropdownList && setDropdownList?.length
                      ? setDropdownList?.map((item: any) => ({
                          label: item?.name,
                          value: item?._id,
                        }))
                      : []),
                  ]}
                  limit={2}
                  setValue={setValue}
                  clearIcon
                  defaultValue={ALL_OPTION}
                  placeHolderText={'Select Project'}
                  addOptionText={'Create new Studio/Prod Co.'}
                  isScroll={true}
                  fetchMoreOptions={() => {
                    if (!loadingSets && hasMoreSets) {
                      setDropdownFilter({
                        ...dropdownfilter,
                        page: dropdownfilter.page + 1,
                      });
                    }
                  }}
                  onInputChange={(
                    event: any,
                    value: string,
                    reason: string,
                  ) => {
                    // ✅ ONLY when user types
                    if (reason !== 'input') return;

                    // ✅ Ignore default "All"
                    if (value === ALL_OPTION.label) return;

                    setSearchInput(value);
                  }}
                  hasMoreOptions={true}
                  isEvent
                  onChangeMethod={(option: any) => {
                    setData([]); // 🔥 reset list
                    setFilter(prev => ({
                      ...prev,
                      page: 1,
                      setId: option?.value === 'ALL' ? '' : option?.value,
                    }));
                  }}
                  dropdownLoading={loadingSets}
                />
              </div>
            </Grid>
            <Grid item md={4} xs={12}>
              <div className="form-in lg">
                <FormControl fullWidth>
                  <div className="f-in">
                    <AutocompleteSelect
                      id="ThreatFilter"
                      labelKey="label"
                      placeHolderText="Threat Level"
                      options={filterOptions}
                      value={
                        filterOptions.find(o => o.id === filter.riskLevel) ??
                        filterOptions[0]
                      }
                      onChange={(event: any, option: any) => {
                        const selected = option?.id ?? 'ALL';
                        setThreatFilter(selected);
                        setFilter(prev => ({
                          ...prev,
                          page: 1,
                          riskLevel: selected,
                        }));
                      }}
                    />
                  </div>
                </FormControl>
              </div>
            </Grid>
          </Grid>
        </div>

        <AddButton />
        <Tabs
          value={value}
          onChange={(e, newVal) => {
            setValue(newVal);
            setData([]);
            setFilter({
              ...filter,
              page: 1,
            });
          }}
          variant="scrollable"
          centered
          scrollButtons={false}
          className="mb-24"
        >
          <Tab
            label={`Active (${tabCounts?.activeSet || 0})`}
            {...TabProps(0)}
          />
          <Tab
            label={`Completed (${tabCounts?.completedSet || 0})`}
            {...TabProps(1)}
          />
          <Tab
            label={`Upcoming (${tabCounts?.inactiveSet || 0})`}
            {...TabProps(2)}
          />

          <Tab label={`All (${tabCounts?.allSet || 0})`} {...TabProps(3)} />
        </Tabs>

        {/* VIEW: TABLE OR GRID */}
        {loading && !isTabular && filter.page === 1 ? (
          <RiskCardLoader />
        ) : loading && filter.page === 1 ? (
          <TableLoader />
        ) : !isTabular ? (
          episodeSetList?.length ? (
            <InfiniteScroll
              isLoading={loading && filter.page > 1}
              hasMore={hasMore}
              onLoadMore={loadMore}
              className="with-title"
              loadingComponent={<RiskCardLoader />}
            >
              <Grid container spacing={2} className="flex-height">
                {episodeSetList?.map((item: any) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={item._id}>
                    <ThreatCard
                      iconClassName="urgent-icon"
                      data={item}
                      isEpisode
                      studioId={studioId}
                    />
                  </Grid>
                ))}
              </Grid>
            </InfiniteScroll>
          ) : (
            <NoData title="No Sets Found" />
          )
        ) : episodeSetList && episodeSetList?.length ? (
          <TableContainer className="reelTable table_height sm-table">
            <Table stickyHeader aria-label="table" className="reelTable">
              <TableHead>
                <TableRow>
                  <TableCell align="left">
                    {productionType?.toLowerCase()?.includes('event')
                      ? 'Activities'
                      : 'Sets'}
                  </TableCell>
                  <TableCell align="left">Threat Level</TableCell>
                  <TableCell align="left">Threat Type</TableCell>
                  <TableCell align="center">Action Items</TableCell>
                  <TableCell align="left">Updated</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {(episodeSetList?.length > 4
                  ? episodeSetList?.slice(0, 8)
                  : episodeSetList
                ).map((item: any) => {
                  const { image, name, locationCount, fallbackImage } = item;

                  const imageUrl =
                    image?.startsWith('http') || image?.startsWith('https')
                      ? image
                      : fallbackImage || Images.VENDOR_PLACEHOLDER;

                  return (
                    <TableRow key={item._id}>
                      <TableCell align="left" sx={{ cursor: 'pointer' }}>
                        <div className="d-flex align-center table-name">
                          <img
                            src={imageUrl}
                            alt={name}
                            className="table-thumb mr-8"
                            onError={e => {
                              e.target.src = Images.VENDOR_PLACEHOLDER;
                            }}
                          />
                          {name.toUpperCase()}
                        </div>
                      </TableCell>

                      <TableCell align="left">
                        <div className="chips shrink border">
                          {(() => {
                            const levels = item?.threatLevels || [];
                            if (!levels.length) return '-';

                            const first = levels[0];
                            const others = levels.slice(1);

                            return (
                              <>
                                {/* FIRST CHIP (same UI as card) */}
                                <span
                                  className={
                                    first?.level === 'CRITICAL'
                                      ? 'red'
                                      : first?.level === 'HIGH'
                                      ? 'orange'
                                      : first?.level === 'MEDIUM'
                                      ? 'yellow'
                                      : first?.level === 'LOW'
                                      ? 'green'
                                      : ''
                                  }
                                >
                                  <i
                                    className={
                                      first?.level === 'CRITICAL'
                                        ? 'urgent-icon'
                                        : first?.level === 'HIGH'
                                        ? 'urgent-icon'
                                        : first?.level === 'MEDIUM'
                                        ? 'medium-icon'
                                        : first?.level === 'LOW'
                                        ? 'low-icon'
                                        : ''
                                    }
                                  ></i>{' '}
                                  {first?.count}{' '}
                                  {formatStringToTitleCase(first?.level)}
                                </span>

                                {/* Tooltip Trigger */}
                                {others.length > 0 && (
                                  <ToolTipComponent
                                    theme="light"
                                    arrow
                                    title={
                                      <div>
                                        {others.map((lvl: any, i: number) => (
                                          <p key={i}>
                                            {lvl?.count}{' '}
                                            {formatStringToTitleCase(
                                              lvl?.level,
                                            )}
                                          </p>
                                        ))}
                                      </div>
                                    }
                                  >
                                    <span className="blue">
                                      +{others.length}
                                    </span>
                                  </ToolTipComponent>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </TableCell>

                      {/* Threat Type Column */}
                      <TableCell align="left">
                        <div className="chips shrink border">
                          {(() => {
                            const types = item?.threatTypes || [];
                            if (!types.length) return '-';

                            const first = types[0];
                            const others = types.slice(1);

                            const maxLength = 24;
                            const truncate = (txt: string) =>
                              txt?.length > maxLength
                                ? txt.slice(0, maxLength) + '...'
                                : txt;

                            return (
                              <>
                                {/* FIRST TRUNCATED CHIP (same UI as card) */}
                                <span className="yellow">
                                  {formatStringToTitleCase(
                                    truncate(first?.type),
                                  )}
                                </span>

                                {/* Tooltip trigger */}
                                {others.length > 0 && (
                                  <ToolTipComponent
                                    theme="light"
                                    arrow
                                    title={
                                      <div>
                                        {others.map((t: any, i: number) => (
                                          <p key={i}>
                                            {formatStringToTitleCase(t?.type)}
                                          </p>
                                        ))}
                                      </div>
                                    }
                                  >
                                    <span className="blue">
                                      +{others.length}
                                    </span>
                                  </ToolTipComponent>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </TableCell>

                      <TableCell align="center" width={'80px'}>
                        {item?.actionItems?.total > 0
                          ? item?.actionItems?.display
                          : '-'}
                      </TableCell>

                      <TableCell align="left" width={'130px'}>
                        {item?.locationCount > 0 ? (
                          <p>
                            Updated on{' '}
                            {`${dayjs(item?.updatedAt).format(
                              'MMM D, YYYY h:mm A',
                            )} ${getTimeWithTimeZoneAbbreviationNew(
                              item?.updatedAt,
                              'z',
                            )}`}
                          </p>
                        ) : (
                          '-'
                        )}
                      </TableCell>

                      <TableCell align="center" width={'120px'}>
                        <button
                          className="blank-btn text-linking underline MuiTypography-subtitle1"
                          onClick={() => {
                            const set = studioId
                              ? [
                                  {
                                    name: 'set',
                                    type: 'set risk dashboard',
                                    title: `${item?.name?.toUpperCase()}`,
                                    path: `${
                                      ROUTES.SET_SECURITY_DASHBOARD
                                    }?setId=${
                                      item?._id
                                    }&name=${item?.name?.toUpperCase()}&studioId=${studioId}`,
                                  },
                                ]
                              : [
                                  {
                                    name: 'set',
                                    type: 'set risk dashboard',
                                    title: `${item?.name?.toUpperCase()}`,
                                    path: `${
                                      ROUTES.SET_SECURITY_DASHBOARD
                                    }?setId=${
                                      item?._id
                                    }&name=${item?.name?.toUpperCase()}`,
                                  },
                                ];
                            setRiskBreadCrumbs(set);

                            if (studioId) {
                              navigate(
                                `/set-security-dashboard/?setId=${
                                  item._id
                                }&name=${item?.name?.toUpperCase()}&studioId=${studioId}`,
                              );
                            } else {
                              navigate(
                                `/set-security-dashboard/?setId=${
                                  item._id
                                }&name=${item?.name?.toUpperCase()}`,
                              );
                            }
                          }}
                        >
                          View Details
                        </button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {episodeSetList?.length && count > 8 && (
              <TablePagination
                limit={8}
                totalRecords={count}
                listData={episodeSetList}
                setPageChange={(val: number) => {
                  updatePaginationPage(val);
                  setFilter({ ...filter, page: val });
                }}
              />
            )}
          </TableContainer>
        ) : (
          <NoData
            title={`No ${
              productionType?.toLowerCase()?.includes('event')
                ? 'Activities'
                : 'Sets'
            } Found`}
          />
        )}
      </section>
    </PageLayout>
  );
};

export default ViewAllEpisodeSets;
